import { nextTick } from 'vue';
import type { NavigationFailure, RouteLocationNormalizedLoaded, RouteLocationRaw, RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import { ADMIN_PAGE_TITLE, DEFAULT_PAGE_TITLE, STORE_PAGE_TITLE, isTestEnv } from '@/utils';
import snackbarPlugin from '@/utils/plugins/snackbar';

export type MoaRouterConfig = RouteRecordRaw & {
  meta: {
    roles: string[];
    title?: string;
    hidden?: boolean;
  };
};

export const routes: RouteRecordRaw[] = [
  {
    // Just for detect '/' path and then redirect to the home page depending on the user role
    path: '/',
    name: 'Home',
    meta: {
      roles: ['everyone'],
      hidden: false,
    },
  },
  {
    path: '/dongles',
    name: 'Dongles',
    component: () => import('@/components/admin/Dongles/Dongles.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  // Cloud Licenses
  {
    path: '/cloud-products',
    name: 'Cloud',
    component: () => import('@/components/admin/CloudProducts/CloudProducts.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  // Users
  {
    path: '/pending-users',
    name: 'pendingUsers',
    component: () => import('@/components/admin/Users/PendingUsers.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/components/admin/Users/Users.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/users/:id',
    name: 'edit-user',
    component: () => import('@/components/admin/Users/EditUser.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  // Organizations
  {
    path: '/organizations',
    name: 'organizations',
    component: () => import('@/components/admin/Organizations/Organizations.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/organizations/new',
    name: 'create-account',
    component: () => import('@/components/admin/Organizations/CreateAccount.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/organizations/:id',
    name: 'edit-organization',
    component: () => import('@/components/admin/Organizations/EditOrganization.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/organizations/:id/invoice-history',
    name: 'organization-invoice-history',
    component: () => import('@/components/admin/Organizations/OrganizationInvoiceHistory.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  // Uploads
  {
    path: '/uploads',
    name: 'uploads',
    component: () => import('@/components/admin/Uploads/Uploads.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/uploads/new',
    name: 'add-upload-link',
    component: () => import('@/components/admin/Uploads/EditUpload.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/uploads/:id',
    name: 'edit-upload-link',
    component: () => import('@/components/admin/Uploads/EditUpload.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  // Demos
  {
    path: '/admin-demos',
    name: 'admin-demos',
    component: () => import('@/components/admin/Demos/DemosList.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/admin-demos/:id',
    name: 'admin-demos-edit',
    component: () => import('@/components/admin/Demos/EditDemo.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  // Discounts
  {
    path: '/discounts',
    name: 'discounts',
    component: () => import('@/components/admin/Discounts/Discounts.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/discounts/new',
    name: 'add-discount',
    component: () => import('@/components/admin/Discounts/EditDiscount.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/discounts/:id',
    name: 'edit-discount',
    component: () => import('@/components/admin/Discounts/EditDiscount.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  // Staff
  {
    path: '/staff',
    name: 'Staff',
    component: () => import('@/components/customer/People/People.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/staff/:id',
    name: 'staff-user-profile',
    component: () => import('@/components/customer/People/UserProfile.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  // Orders
  {
    path: '/orders',
    name: 'orders',
    component: () => import('@/components/admin/Orders/Orders.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/orders/new',
    name: 'add-order',
    component: () => import('@/components/admin/Orders/EditOrder.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/orders/:orderId',
    name: 'edit-order',
    component: () => import('@/components/admin/Orders/EditOrder.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/inventory',
    name: 'Inventory',
    component: () => import('@/components/admin/Inventory/Inventory.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/email-templates',
    name: 'email-templates',
    component: () => import('@/components/admin/Emails/Emails.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/maintenance-windows',
    name: 'maintenance-windows',
    component: () => import('@/components/admin/Maintenance/MaintenanceWindows.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/email-templates/:id',
    name: 'edit-email-template',
    component: () => import('@/components/admin/Emails/EditEmail.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/pending-applicants',
    name: 'pending-applicants',
    component: () => import('@/components/admin/PendingApplicants/PendingApplicants.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/pending-applicants/:id',
    name: 'applicant-review',
    component: () => import('@/components/admin/PendingApplicants/ApplicantReview.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/schools',
    name: 'schools',
    component: () => import('@/components/admin/Schools/Schools.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/schools/:id',
    name: 'edit-school',
    component: () => import('@/components/admin/Schools/EditSchool.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },
  {
    path: '/schools/new',
    name: 'add-school',
    component: () => import('@/components/admin/Schools/EditSchool.vue'),
    meta: {
      roles: ['admin'],
      title: ADMIN_PAGE_TITLE,
    },
  },

  // Shop
  {
    path: '/shop',
    name: 'shop',
    component: () =>
      import(
        /* webpackChunkName: "Shop" */
        '@/components/shop/Front/Front.vue'
      ),
    meta: {
      roles: ['owner', 'guest', 'unauthorized'],
      title: STORE_PAGE_TITLE,
    },
  },
  // {
  //   path: '/shopsupersecret557734598',
  //   name: 'shopSuperSecret',
  //   component: () =>
  //     import(
  //       /* webpackChunkName: "Shop" */
  //       '@/components/shop/Front/FrontHidden.vue'
  //     ),
  //   meta: {
  //     roles: ['owner', 'guest', 'unauthorized'],
  //     title: STORE_PAGE_TITLE,
  //   },
  // },
  {
    path: '/shop/cart',
    name: 'shop-cart',
    component: () => import('@/components/shop/Cart/Cart.vue'),
    meta: {
      roles: ['owner', 'guest'],
      title: STORE_PAGE_TITLE,
    },
  },
  {
    path: '/shop/checkout',
    name: 'shop-checkout',
    component: () => import('@/components/shop/Checkout/Checkout.vue'),
    meta: {
      roles: ['owner', 'guest'],
      title: STORE_PAGE_TITLE,
    },
  },
  {
    path: '/shop/receipt/:orderId',
    name: 'shop-receipt',
    component: () => import('@/components/shop/Receipt/Receipt.vue'),
    meta: {
      roles: ['owner', 'member', 'guest'],
      title: STORE_PAGE_TITLE,
    },
  },
  {
    path: '/shop/payment-auth/:orderId',
    name: 'paymentAuth',
    component: () =>
      import(
        /* webpackChunkName: "paymentMethods" */
        '@/components/shop/PaymentAuth/PaymentAuth.vue'
      ),
    meta: {
      roles: ['owner', 'guest'],
    },
  },
  {
    path: '/shop/checkout-order/:orderId',
    name: 'orderCheckoutRedirect',
    component: () =>
      import(
        /* webpackChunkName: "paymentMethods" */
        '@/components/shop/Checkout/RedirectToCheckout.vue'
      ),
    meta: {
      roles: ['owner', 'guest'],
    },
  },
  // Login
  {
    path: '/auth/forgot-password',
    name: 'loginForgotPassword',
    component: () => import('@/components/auth/ResetPassword/ResetPassword.vue'),
    meta: {
      roles: ['everyone'],
    },
  },
  {
    path: '/registration/:invitationToken',
    name: 'loginRegistrationWithToken',
    component: () => import('@/components/auth/Registration/Registration.vue'),
    meta: {
      roles: ['unauthorized'],
    },
  },
  {
    path: '/join/:invitationToken',
    name: 'join',
    component: () => import('@/components/auth/Join/Join.vue'),
    meta: {
      roles: ['everyone'],
    },
  },
  {
    path: '/registration',
    name: 'loginRegistration',
    component: () => import('@/components/auth/Registration/Registration.vue'),
    meta: {
      roles: ['unauthorized'],
    },
  },
  {
    path: '/auth/set-new-password/:token',
    name: 'loginSetNewPassword',
    component: () => import('@/components/auth/ResetPassword/SetNewPassword.vue'),
    meta: {
      roles: ['everyone'],
    },
  },
  {
    path: '/auth/change-password',
    name: 'changePassword',
    component: () => import('@/components/auth/ChangePassword/ChangePassword.vue'),
    meta: {
      roles: ['guest', 'owner', 'member', 'admin'],
    },
  },
  {
    path: '/signin',
    name: 'loginSignin',
    component: () => import('@/components/auth/Signin/Signin.vue'),
    meta: {
      roles: ['unauthorized'],
    },
  },
  {
    path: '/auth/signout',
    name: 'loginSignout',
    component: () => import('@/components/auth/Signout/Signout.vue'),
    meta: {
      roles: ['owner', 'guest', 'member', 'admin', 'emptyRole'],
    },
  },
  {
    path: '/reauth',
    name: 'reauth',
    component: () => import('@/components/auth/Reauthorization/Reauthorization.vue'),
    meta: {
      roles: ['owner', 'guest', 'member'],
    },
  },
  {
    path: '/reauth-or-login',
    name: 'reauthOrLogin',
    component: () => import('@/components/auth/Reauthorization/ReauthOrLogin.vue'),
    meta: {
      roles: ['owner', 'guest', 'member', 'unauthorized'],
    },
  },
  // Downloads
  {
    path: '/downloads',
    name: 'downloads',
    component: () => import('@/components/customer/Downloads/Downloads.vue'),
    meta: {
      roles: ['owner', 'guest', 'member'],
    },
  },
  {
    path: '/downloads/:id',
    name: 'product-downloads',
    component: () => import('@/components/customer/Downloads/ProductDownloads.vue'),
    meta: {
      roles: ['owner', 'guest', 'member'],
    },
  },
  {
    path: '/downloads/:id/info',
    name: 'product-info',
    component: () => import('@/components/customer/Downloads/DownloadInfo.vue'),
    meta: {
      roles: ['owner', 'guest', 'member'],
    },
  },
  // People
  {
    path: '/people',
    name: 'people',
    component: () => import('@/components/customer/People/People.vue'),
    meta: {
      roles: ['owner'],
    },
  },
  {
    path: '/students',
    name: 'students',
    component: () => import('@/components/customer/People/Students.vue'),
    meta: {
      roles: ['owner', 'guest'],
    },
  },
  {
    path: '/people/:id',
    name: 'user-profile',
    component: () => import('@/components/customer/People/UserProfile.vue'),
    meta: {
      roles: ['guest', 'owner', 'member', 'admin'],
    },
  },
  {
    path: '/invoices',
    name: 'invoices',
    component: () => import('@/components/customer/Invoices/Invoices.vue'),
    meta: {
      roles: ['owner'],
    },
  },
  {
    path: '/download-invoice/:token',
    name: 'invoiceDownload',
    component: () => import('@/components/shop/Invoice/InvoiceDownload.vue'),
    meta: {
      roles: ['everyone'],
    },
  },
  {
    path: '/download-report-csv/:path',
    name: 'report-csv-download',
    component: () => import('@/components/shop/Invoice/ReportCsvDownload.vue'),
    meta: {
      roles: ['everyone'],
    },
  },
  // Products
  {
    path: '/products',
    name: 'products',
    component: () => import('@/components/customer/Products/Products.vue'),
    meta: {
      roles: ['owner', 'member', 'guest'],
    },
  },
  {
    path: '/free-trial',
    name: 'free-trial',
    component: () => import('@/components/customer/Demos/FreeTrial.vue'),
    meta: {
      roles: ['guest', 'owner'],
    },
  },
  // Demo
  // {
  //   path: '/demo/:demoLinkName',
  //   name: 'download-demo',
  //   component: () => import('@/components/customer/Demos/DemoDownload.vue'),
  //   meta: {
  //     roles: ['owner', 'member', 'guest'],
  //   },
  // },
  {
    path: '/viewer',
    name: 'viewer',
    component: () => import('@/components/customer/Demos/ViewerDownload.vue'),
    meta: {
      roles: ['owner', 'member', 'guest'],
    },
  },
  // {
  //   path: '/demo/:demoLinkName/result',
  //   name: 'download-demo-result',
  //   component: () => import('@/components/customer/Demos/DemoDownloadResult.vue'),
  //   meta: {
  //     roles: ['owner', 'member', 'guest'],
  //   },
  // },
  {
    path: '/viewer/:demoLinkName/result',
    name: 'download-viewer-result',
    component: () => import('@/components/customer/Demos/DemoDownloadResult.vue'),
    meta: {
      roles: ['owner', 'member', 'guest'],
    },
  },
  {
    path: '/organization',
    name: 'organization',
    component: () => import('@/components/customer/Organization/Organization.vue'),
    meta: {
      roles: ['owner', 'emptyRole'],
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/components/customer/People/MyProfile.vue'),
    meta: {
      roles: ['owner', 'member', 'admin', 'guest'],
    },
  },
  {
    path: '/access-denied',
    name: '403',
    component: () => import('@/components/shared/common/Error403.vue'),
    meta: {
      roles: ['everyone'],
    },
  },
  {
    path: '/support-request',
    name: 'supportRequest',
    component: () => import('@/components/customer/TechSupport/TechSupportForm.vue'),
    meta: {
      roles: ['owner', 'member'], //
    },
  },
  {
    path: '/payment-method-setup/:cloudProductId',
    name: 'paymentMethodSetup',
    component: () =>
      import(
        /* webpackChunkName: "paymentMethods" */
        '@/components/customer/Products/PaymentMethodSetup.vue'
      ),
    meta: {
      roles: ['owner', 'guest'],
    },
  },
  {
    path: '/dongle-payment-method-setup/:dongleId',
    name: 'donglePaymentMethodSetup',
    component: () =>
      import(
        /* webpackChunkName: "paymentMethods" */
        '@/components/customer/Products/DonglePaymentMethodSetup.vue'
      ),
    meta: {
      roles: ['owner', 'guest'],
    },
  },
  {
    path: '/payment-methods',
    name: 'paymentMethods',
    component: () =>
      import(
        /* webpackChunkName: "paymentMethods" */
        '@/components/customer/PaymentMethods/PaymentMethods.vue'
      ),
    meta: {
      roles: ['owner', 'guest'],
    },
  },
  {
    path: '/phone-validation',
    name: 'phoneValidation',
    component: () => import('@/components/auth/PhoneValidation/PhoneValidation.vue'),
    meta: {
      roles: ['owner', 'guest', 'member', 'emptyRole'],
    },
  },
  {
    path: '/dongle-web-simulator',
    name: 'dongle-web-simulator',
    component: () => import('@/components/admin/Dongles/DongleWebSimulator.vue'),
    meta: {
      roles: ['everyone'],
    },
  },
  {
    path: '/page-not-found',
    name: '404',
    component: () => import('@/components/shared/common/Error404.vue'),
    meta: {
      roles: ['everyone'],
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/page-not-found',
  },
].filter(route => !route.meta?.hidden) as RouteRecordRaw[];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

export const getHomePath = function (): string {
  if (store.getters['login/loggedIn']) {
    if (store.getters['login/isAdmin']) {
      return '/dongles';
    }
    if (store.getters['login/isMember']) {
      return '/products';
    } else if (store.getters['login/isEmptyRole']) {
      return '/organization';
    }
    return '/products';
  }
  return '/shop';
};

function roleIsCorrect(to: MoaRouterConfig): boolean {
  return (
    (store.getters['login/isAdmin'] && to.meta?.roles.includes('admin')) ||
    (store.getters['login/isOwner'] && to.meta?.roles.includes('owner')) ||
    (store.getters['login/isDemo'] && to.meta?.roles.includes('guest')) ||
    (store.getters['login/isMember'] && to.meta?.roles.includes('member')) ||
    (store.getters['login/isEmptyRole'] && to.meta?.roles.includes('emptyRole'))
  );
}

function getRouteRoles(to: unknown): string[] {
  return (to as unknown as MoaRouterConfig)?.meta?.roles || [];
}

const originalPush = router.push;
// skipcq: JS-0333
router.push = (location: RouteLocationRaw): Promise<void | NavigationFailure | undefined> =>
  originalPush.call(router, location).catch(err => err);

// Note we ignoring to.query to have possibility to set { query: { search: 'value' } } in the router.push() and avoid
// page refresh, this can be changed by using <router-view :key="$route.fullPath" /> in the App.vue
router.beforeEach(async (to, from) => {
  // If particular path is not specified, redirect to the home page, depending on the user role
  if (to.fullPath === '/') {
    return { path: getHomePath() };
  }
  // Dongle web simulator is only available in test environment
  if (to.name === 'dongle-web-simulator' && !isTestEnv()) {
    return { path: '/page-not-found' };
  }
  // Inactive users can only access registration and signout
  if (
    store.getters['login/loggedIn'] &&
    store.getters['login/user'] &&
    !store.getters['login/user'].active &&
    to.path !== '/registration' &&
    to.path !== '/support-request' &&
    to.name !== 'loginSignout'
  ) {
    const query = store.getters['login/user'].isEducational ? { edu: 'true' } : {};
    return { path: '/registration', query };
  }
  // Partial edu registration users must continue their edu registration
  if (store.getters['login/loggedUserEduFields'] && to.name !== 'loginSignout') {
    if (to.path !== '/registration' || to.query.edu !== 'true') {
      return { path: '/registration', query: { edu: 'true' } };
    }
  }
  if ((store.getters['login/loggedIn'] || store.getters['login/loggedUserEduFields']) && to.name === 'loginSignout') {
    return true;
  }
  // Keep previous route for the back button
  await store.dispatch('login/setPrevRoute', {
    name: from.name,
    path: from.path,
    fullPath: from.fullPath,
    query: from.query,
  } as RouteLocationNormalizedLoaded);

  // EVERYONE
  const routeRoles = getRouteRoles(to);
  if (JSON.stringify(routeRoles) === JSON.stringify(['everyone'])) {
    return true;
    // UNAUTHORIZED ONLY
  } else if (JSON.stringify(routeRoles) === JSON.stringify(['unauthorized'])) {
    if (store.getters['login/loggedIn']) {
      if (store.getters['login/user'] && !store.getters['login/user'].active) {
        return true;
      }
      // if (to.name === 'loginRegistrationWithToken') {
      //   await store.dispatch('login/getUserByInvitation', to.params.invitationToken);
      // }
      return { path: getHomePath() };
    }
    if (
      !to.query.redirect &&
      !['/'].includes(from.fullPath) &&
      !['shop', 'loginSetNewPassword', 'loginRegistrationWithToken', 'join'].includes(from.name as string) &&
      from.name !== 'loginSignout'
    ) {
      to.query.redirect = from.fullPath;
    }
    return true;

    // AUTHORIZED: OWNER, MEMBER, DEMO, ADMIN
  } else if (
    ['owner', 'member', 'guest', 'admin', 'emptyRole', 'unauthorized'].find(role => routeRoles.includes(role))
  ) {
    if (store.getters['login/loggedIn']) {
      if (roleIsCorrect(to as unknown as MoaRouterConfig)) {
        return true;
      }
      if (to.name === 'shop-cart' && store.getters['login/isMember']) {
        snackbarPlugin.error(
          'You are logged in as a company member. You can only buy the product if you are the owner of the ' +
            'company. If you want to purchase a product, please log out and create an account as the owner of the ' +
            'company.',
          3600,
        );
      }
      return { path: getHomePath() };
    }
    // UNAUTHORIZED ALLOWED
    if (routeRoles.includes('unauthorized')) {
      return true;
    }
    const redirect = to.name === 'loginSignout' ? undefined : to.fullPath;
    return { path: '/signin', query: redirect ? { redirect } : {} };

    // UNKNOWN ROLE OR NO ROUTE FOUND
  }
  // If no roles defined (route not found), redirect to 404
  if (routeRoles.length === 0) {
    return { path: '/page-not-found' };
  }
  console.error('Error in role based access control');
  return { path: '/access-denied' };
});

router.afterEach(async to => {
  await nextTick((): void => {
    if (to.fullPath === getHomePath()) {
      if (store.getters['login/isAdmin']) {
        document.title = ADMIN_PAGE_TITLE;
      } else if (store.getters['login/isOwner'] || store.getters['login/isDemo']) {
        document.title = STORE_PAGE_TITLE;
      } else {
        document.title = DEFAULT_PAGE_TITLE;
      }
    } else {
      document.title = (to as unknown as MoaRouterConfig).meta.title || DEFAULT_PAGE_TITLE;
    }
  });
});

export default router;
